import React, { useRef, useEffect, Suspense } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import HierarchyViewComponent from './HierarchyViewComponent';
import { allAssetTypes, apiCallStatus } from 'helpers/constants';
import * as actions from './actions';
import { getModel } from './selectors';
import { LoadingStatusWrapper } from 'sharedComponents/WithLoadingStatus';
import { LoadingIndicator } from 'sharedComponents/PowertrainLoadingIndicator';
import { conditionIndicesViewOptions, toggleButtonOptions } from '../constants';
import { setAssetSyncFlag } from '../../../GeneralFilter/GeneralFilterModal/actions';

// Module-level helper — keeps useEffect nesting within limits.
const buildPayload = (filter, isHierarchy, organizationId, selectedFilters) => {
    const sensorIdentifiers = filter?.sensorIdentifierFilter
        ? filter.sensorIdentifierFilter.split(',').map(s => s.trim()).filter(s => s.length > 0)
        : [];
    const siteIds = selectedFilters?.sitesChecked
        ? (selectedFilters.sites || []).map(site => site.siteID)
        : [];
    return {
        organizationId,
        siteIds,
        isIncludeLocationsOnly: isHierarchy,
        isFlat: !isHierarchy,
        assetGroupId: null,
        conditionFilter: filter?.conditionFilter || [],
        assetTypeFilter: filter?.assetTypeFilter === allAssetTypes ? '' : filter?.assetTypeFilter || '',
        subscriptionTypeFilter: filter?.subscriptionTypeFilter || [],
        sensorIdentifiers,
        sortingOrder: 'asc',
        ...filter?.searchBox?.trim() ? { filter: filter.searchBox.trim() } : {}
    };
};

const HierarchyViewContainer = (props) => {

    const {
        selectedAsset,
        leftPanelSize,
        allAssets,
        conditionIndicesView,
        showFilter,
        onSelectAsset,
        generalFilterLoadingStatuses,
        activeTab,
        selectedOrganization,
        expandCollapseCommand,
        selectedAssetIds,
        selectedAssetSignalCounts
    } = props;

    const {
        hierarchyData,
        hierarchyLoadingStatus,
        hierarchyInitialLoadDone,
        assetOverviewData,
        assetOverviewLoadingStatus,
        assetInitialLoadDone,
        refeshCIAMSync,
        refreshAssets,
        selectedFilters
    } = props.model;

    const {
        getHierarchyConditionIndices,
        previousSearchFilter
    } = props.actions;

    const dispatch = useDispatch();
    const organizationId = selectedOrganization?.organizationID;

    // Read each view's filter slice independently from the store so that each
    // API request uses its own correct filter values (only the search box is shared).
    const listFilter = useSelector(state => state.conditionIndicesV2?.filter?.assets);
    const hierarchyFilter = useSelector(state => state.conditionIndicesV2?.filter?.hierarchy);

    const timerRef = useRef(null);
    const ciamRefreshRef = useRef({ organizationId: null, requestedViews: {} });

    const getSelectedTabData = () => {
        if (conditionIndicesView === conditionIndicesViewOptions.HIERARCHY) {
            return hierarchyData;
        } else if (conditionIndicesView === conditionIndicesViewOptions.LIST && activeTab === toggleButtonOptions.ASSETS) {
            return assetOverviewData;
        }
    };

    const getSelectedTabLoadingStatus = () => {
        if (conditionIndicesView === conditionIndicesViewOptions.HIERARCHY) {
            return hierarchyLoadingStatus;
        } else if (conditionIndicesView === conditionIndicesViewOptions.LIST && activeTab === toggleButtonOptions.ASSETS) {
            return assetOverviewLoadingStatus;
        }
    };

    useEffect(() => {
        if (!organizationId) { return; }

        if (!refeshCIAMSync || ciamRefreshRef.current.organizationId !== organizationId) {
            ciamRefreshRef.current = { organizationId, requestedViews: {} };
        }
        const requestedViews = ciamRefreshRef.current.requestedViews;

        // Check each view independently — avoid re-requesting if already in-flight
        const needsHierarchyFetch = hierarchyLoadingStatus !== apiCallStatus.LOADING && (!hierarchyInitialLoadDone || refeshCIAMSync && !requestedViews[conditionIndicesViewOptions.HIERARCHY] || refreshAssets);
        const needsListFetch = assetOverviewLoadingStatus !== apiCallStatus.LOADING && (!assetInitialLoadDone || refeshCIAMSync && !requestedViews[conditionIndicesViewOptions.LIST] || refreshAssets);

        const shouldFetch = conditionIndicesView === conditionIndicesViewOptions.HIERARCHY
            ? needsHierarchyFetch
            : conditionIndicesView === conditionIndicesViewOptions.LIST && activeTab === toggleButtonOptions.ASSETS && needsListFetch;
        if (!shouldFetch) { return; }

        if (timerRef?.current) {
            clearTimeout(timerRef?.current);
        }

        // Shared search box value (both views are always in sync for this)
        const sharedSearchBox = listFilter?.searchBox || '';

        timerRef.current = setTimeout(() => {
            requestedViews[conditionIndicesView] = true;
            if (needsListFetch && conditionIndicesView === conditionIndicesViewOptions.LIST && activeTab === toggleButtonOptions.ASSETS) {
                getHierarchyConditionIndices(buildPayload(listFilter, false, organizationId, selectedFilters), conditionIndicesViewOptions.LIST);
            }
            if (needsHierarchyFetch && conditionIndicesView === conditionIndicesViewOptions.HIERARCHY) {
                getHierarchyConditionIndices(buildPayload(hierarchyFilter, true, organizationId, selectedFilters), conditionIndicesViewOptions.HIERARCHY);
            }
            dispatch(setAssetSyncFlag());
            previousSearchFilter(sharedSearchBox);
        }, sharedSearchBox.length > 0 ? 700 : 0);

        return () => {
            if (timerRef?.current) {
                clearTimeout(timerRef?.current);
            }
        };
    }, [conditionIndicesView, activeTab, listFilter, hierarchyFilter, hierarchyInitialLoadDone, assetInitialLoadDone, hierarchyLoadingStatus, assetOverviewLoadingStatus, refeshCIAMSync, refreshAssets, organizationId, selectedFilters, dispatch, getHierarchyConditionIndices, previousSearchFilter]);

    return <LoadingStatusWrapper loadingStatus={[getSelectedTabLoadingStatus(), ...generalFilterLoadingStatuses]}>
        <Suspense fallback={<LoadingIndicator />}>
            <HierarchyViewComponent
                selectedAsset={selectedAsset}
                leftPanelSize={leftPanelSize}
                allAssets={allAssets}
                conditionIndicesView={conditionIndicesView}
                showFilter={showFilter}
                onSelectAsset={onSelectAsset}
                hierarchyData={getSelectedTabData()}
                hierarchyLoadingStatus={getSelectedTabLoadingStatus()}
                expandCollapseCommand={expandCollapseCommand}
                selectedAssetIds={selectedAssetIds}
                selectedAssetSignalCounts={selectedAssetSignalCounts}
            />
        </Suspense>
    </LoadingStatusWrapper>;
};

HierarchyViewContainer.propTypes = {
    selectedAsset: PropTypes.object,
    leftPanelSize: PropTypes.number,
    allAssets: PropTypes.array,
    conditionIndicesView: PropTypes.string,
    showFilter: PropTypes.bool,
    onSelectAsset: PropTypes.func,
    generalFilterLoadingStatuses: PropTypes.array,
    activeTab: PropTypes.string,
    selectedOrganization: PropTypes.object,
    expandCollapseCommand: PropTypes.shape({
        mode: PropTypes.string,
        nonce: PropTypes.number
    }),
    selectedAssetIds: PropTypes.array,
    selectedAssetSignalCounts: PropTypes.object,
    model: PropTypes.shape({
        hierarchyData: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
        hierarchyLoadingStatus: PropTypes.string,
        hierarchyInitialLoadDone: PropTypes.bool,
        assetOverviewData: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
        assetOverviewLoadingStatus: PropTypes.string,
        assetInitialLoadDone: PropTypes.bool,
        refeshCIAMSync: PropTypes.bool,
        refreshAssets: PropTypes.bool,
        selectedFilters: PropTypes.shape({
            sitesChecked: PropTypes.bool,
            sites: PropTypes.arrayOf(PropTypes.shape({
                siteID: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
            }))
        })
    }),
    actions: PropTypes.shape({
        getHierarchyConditionIndices: PropTypes.func,
        previousSearchFilter: PropTypes.func
    })
};

const mapStateToProps = (state) => {
    const model = getModel(state);
    return {
        model
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        actions: bindActionCreators(actions, dispatch)
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(HierarchyViewContainer);
